import { createPdf } from '@project/utils/createPdf';
import { Content, ContentImage } from 'pdfmake/interfaces';
import { Map, View } from 'ol';
import { HeatmapStyles } from '@project/store/HeatmapLayerCreator';
import { Extent } from 'ol/extent';
import { getMapCanvas, getMapExtentCanvas } from '@utils/map';
import { DocumentOrientation } from '@project/components/Module/HeatMap/types';
import TileLayer from 'ol/layer/Tile';
import VectorSource from 'ol/source/Vector';
import HeatmapLayer from 'ol/layer/Heatmap';
import { XYZ } from 'ol/source';
import { BACKGROUND_TILE_URL } from '@project/store/BaseLayersStore';
import { INIT_MAP_DATA } from '@project/store';

export const createPdfScreenshotSection = (
  image: string,
  orientation?: DocumentOrientation,
): ContentImage => {
  if (orientation === 'landscape') {
    return { image, width: 700, alignment: 'center', pageBreak: 'after' };
  } else {
    return { image, width: 570, alignment: 'center' };
  }
};

const OFFSET_LEFT_POSITION = '-10000px';

export const createHeatMapScreenshot = (
  heatLayer: HeatmapLayer,
  baseLayer: TileLayer,
  heatMapStyle: HeatmapStyles,
  extent?: Extent,
) => {
  const promise = new Promise<string>((resolve, reject) => {
    const tempContainer = document.createElement('div');
    tempContainer.style.width = '100vw';
    tempContainer.style.height = '100vh';
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = `${OFFSET_LEFT_POSITION}`;
    tempContainer.style.zIndex = '900';
    document.body.appendChild(tempContainer);

    const source = baseLayer.getSource();

    if (!source) {
      return reject(new Error('Не удалось получить источник данных'));
    }

    const tileLayerCopy = new TileLayer({
      source: source,
      opacity: baseLayer.getOpacity(),
      visible: baseLayer.getVisible(),
      zIndex: baseLayer.getZIndex() ?? 0,
    });

    const originalSource = heatLayer.getSource();

    if (!originalSource) {
      return reject(new Error('Не удалось получить источник данных'));
    }

    const newSource = new VectorSource({
      features: originalSource.getFeatures().map((f) => f.clone()),
    });

    const heatmapLayerCopy = new HeatmapLayer({
      ...heatLayer.getProperties(),
      source: newSource,
      gradient: heatMapStyle.gradient,
      blur: heatMapStyle.blur,
      radius: heatMapStyle.radius,
    });

    const layers = [tileLayerCopy, heatmapLayerCopy];

    if (baseLayer.get('name') === 'Donland_gisro_map') {
      layers.push(
        new TileLayer({
          source: new XYZ({
            url: BACKGROUND_TILE_URL,
            crossOrigin: 'anonymous',
          }),
          zIndex: 0,
        }),
      );
    }

    const tempMap = new Map({
      target: tempContainer,
      layers: layers,
      view: new View({
        center: INIT_MAP_DATA.center,
        zoom: INIT_MAP_DATA.zoom,
      }),
    });

    tempMap.once('rendercomplete', async function () {
      setTimeout(async () => {
        const mapCanvas = extent
          ? await getMapExtentCanvas(tempMap, extent)
          : getMapCanvas(tempMap);
        const size = tempMap.getSize();

        if (size) {
          const [width, height] = size;

          if (width === undefined || height === undefined) {
            return reject(new Error('Не удалось получить размеры карты'));
          }

          mapCanvas.width = width;
          mapCanvas.height = height;
        }
        const mapContext = mapCanvas.getContext('2d');

        if (!mapContext) return reject();

        Array.prototype.forEach.call(
          tempMap
            .getViewport()
            .querySelectorAll('.ol-layer canvas, canvas.ol-layer'),
          function (canvas) {
            const opacity =
              canvas.parentNode.style.opacity || canvas.style.opacity;
            mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
            let matrix;
            const transform = canvas.style.transform;
            if (transform) {
              // Get the transform parameters from the style's transform matrix
              matrix = transform
                .match(/^matrix\(([^\(]*)\)$/)[1]
                .split(',')
                .map(Number);
            } else {
              matrix = [
                parseFloat(canvas.style.width) / canvas.width,
                0,
                0,
                parseFloat(canvas.style.height) / canvas.height,
                0,
                0,
              ];
            }
            // Apply the transform to the export map context
            CanvasRenderingContext2D.prototype.setTransform.apply(
              mapContext,
              matrix,
            );
            const backgroundColor = canvas.parentNode.style.backgroundColor;
            if (backgroundColor) {
              mapContext.fillStyle = backgroundColor;
              mapContext.fillRect(0, 0, canvas.width, canvas.height);
            }
            mapContext.drawImage(canvas, 0, 0);
          },
        );
        mapContext.globalAlpha = 1;
        mapContext.setTransform(1, 0, 0, 1, 0, 0);

        const result = mapCanvas.toDataURL('image/png');

        tempMap.setTarget(undefined);
        document.body.removeChild(tempContainer);

        resolve(result);
      }, 400);
    });
    tempMap.renderSync();
  });

  return promise;
};

export const createHeatMapPdf = ({
  title,
  screenshot,
  styles,
  orientation,
}: {
  title: string;
  screenshot: string;
  styles: HeatmapStyles;
  orientation: DocumentOrientation;
}) => {
  const { gradient, blur, radius } = styles;

  const screenShotSection = createPdfScreenshotSection(screenshot, orientation);

  const documentContent: Content = [
    {
      text: title,
      style: 'documentTitle',
    },
    screenShotSection ?? [],
    {
      text: 'Легенда',
      style: 'documentTitle',
    },
    {
      table: {
        headerRows: 1,
        widths: [170, '*'],
        heights: [
          15,
          15,
          orientation === 'landscape' ? 15 : 30,
          15,
          15,
          15,
          15,
          15,
        ],
        body: [
          [{ text: 'Название' }, { text: title }],
          [{ text: 'Радиус зоны действия' }, { text: radius }],
          [{ text: 'Размытие' }, { text: blur }],

          [{ text: 'min' }, { text: null, fillColor: gradient[0] }],
          [{ text: '' }, { text: null, fillColor: gradient[1] }],
          [{ text: '' }, { text: null, fillColor: gradient[2] }],
          [{ text: '' }, { text: null, fillColor: gradient[3] }],
          [{ text: 'max' }, { text: null, fillColor: gradient[4] }],
        ],
      },
    },
  ];

  return createPdf(documentContent, {
    info: { title: 'Тепловая карта' },
    pageOrientation: orientation,
  });
};
